import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { PropertyFormTabs } from './PropertyFormTabs';
import { GeneralInfo } from './tabs/GeneralInfo';
import { PropertyDetails } from './tabs/PropertyDetails';
import { Criteria } from './tabs/Criteria';
import { Description } from './tabs/Description';
import { Photos } from './tabs/Photos';
import { Documents } from './tabs/Documents';
import { Matching } from './tabs/Matching';
import { usePropertyFormTabs } from '../../../hooks/usePropertyFormTabs';
import { PropertyFormProvider } from './contexts/PropertyFormContext';
import { PhotoProvider } from './contexts/PhotoContext';
import { DocumentProvider } from './contexts/DocumentContext';
import { useScrollToTop } from '../../../hooks/useScrollToTop';
import { useProperties } from '../../../hooks/useProperties';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { validatePropertyData } from '../../../utils/propertyValidation';
import { usePropertyFormContext } from './contexts/PropertyFormContext';
import { usePhotoContext } from './contexts/PhotoContext';

const PropertyEditFormContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeTab, setActiveTab, goToNextTab } = usePropertyFormTabs();
  const { getPropertyById, updateProperty, properties, isLoading: propertiesLoading } = useProperties();
  const { user } = useAuth();
  const { formData } = usePropertyFormContext();
  const { photos } = usePhotoContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [property, setProperty] = useState<any>(null);

  useScrollToTop([activeTab]);

  useEffect(() => {
    // Attendre que les propriétés soient chargées
    if (propertiesLoading) {
      return;
    }

    if (id) {
      // Si les propriétés ne sont pas encore là, attendre un peu plus
      if (properties.length === 0) {
        console.log('PropertyEditForm - Properties still empty, waiting...');
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return;
      }

      // Utiliser l'ID comme string (pas besoin de conversion)
      const foundProperty = getPropertyById(id);
      
      if (foundProperty) {
        console.log('PropertyEditForm - Property found:', foundProperty);
        setProperty(foundProperty);
      } else {
        console.log('PropertyEditForm - Available IDs:', properties.map(p => p.id));
        console.log('PropertyEditForm - Looking for ID:', id);
        console.log('PropertyEditForm - Property not found, redirecting...');
        navigate('/');
      }
      setIsLoading(false);
    }
  }, [id, getPropertyById, navigate, propertiesLoading, properties]);

  const handleSave = async () => {
    if (isSubmitting || !property) return;
    
    setIsSubmitting(true);
    
    try {
      console.log('Données du formulaire de modification:', formData);
      console.log('Photos disponibles:', photos);
    
      // Validation des champs obligatoires
      if (!formData.propertyType) {
        alert('Veuillez sélectionner un type de bien');
        setIsSubmitting(false);
        return;
      }
      if (!formData.propertyAddress) {
        alert('Veuillez saisir l\'adresse du bien');
        setIsSubmitting(false);
        return;
      }
      if (!formData.ownerFirstName || !formData.ownerLastName) {
        alert('Veuillez saisir le nom du propriétaire');
        setIsSubmitting(false);
        return;
      }
      if (!formData.sellerPrice) {
        alert('Veuillez saisir le prix');
        setIsSubmitting(false);
        return;
      }
      if (!formData.livingArea && !formData.totalArea) {
        alert('Veuillez saisir la surface');
        setIsSubmitting(false);
        return;
      }
      if (!formData.roomCount) {
        alert('Veuillez saisir le nombre de pièces');
        setIsSubmitting(false);
        return;
      }

      // Préparer les URLs des photos
      const photoUrls = photos
        .filter(photo => photo.forWeb && photo.progress === 100 && !photo.error) // Seulement les photos complètement uploadées
        .map(photo => photo.serverUrl || photo.preview); // Priorité à l'URL du serveur

      console.log('📸 Photos à sauvegarder pour la modification:', photoUrls);
      console.log('📸 État des photos:', photos.map(p => ({ id: p.id, progress: p.progress, serverUrl: p.serverUrl, error: p.error })));

      // Logs de debug pour voir l'état du formData
      console.log('🔍 [EDIT-FORM] État formData avant envoi:');
      console.log('  📋 general_condition:', formData.generalCondition, 'condition:', formData.condition);
      console.log('  📋 living_room_area:', formData.livingRoomArea);
      console.log('  📋 total_floors:', formData.totalFloors);
      console.log('  📋 kitchen_type:', formData.kitchenType);
      console.log('  📋 bathrooms:', formData.bathroomCount, formData.bathrooms);
      console.log('  📋 has_roof_terrace:', formData.hasRoofTerrace);
      console.log('  📋 has_gas_heating:', formData.hasGasHeating);
      console.log('  📋 has_private_parking:', formData.hasPrivateParking);
      console.log('  📋 has_digicode:', formData.hasDigicode);
      console.log('  📋 has_cistern:', formData.hasCistern);
      console.log('  📋 has_water_tank:', formData.hasWaterTank);
      console.log('  📋 has_double_glazing:', formData.hasDoubleGlazing);
      console.log('  📋 has_electric_shutters:', formData.hasElectricShutters);

      // Préparer les données de mise à jour
      console.log('🔍 [EDIT-FORM] Status original du bien:', property.status);
      console.log('🔍 [EDIT-FORM] TransactionType du formData:', formData.transactionType);

      const rawPropertyData = {
        property_category: formData.propertyType,
        property_subtype: formData.apartmentType || formData.houseType || 'Simple',
        address: formData.propertyAddress,
        city: formData.propertyCity || formData.propertyAddress.split(',').pop()?.trim() || 'Non spécifié',
        wilaya: formData.propertyWilaya || 'Non spécifié',
        price: parseFloat(formData.salePrice?.replace(/\s/g, '')) || parseFloat(formData.sellerPrice?.replace(/\s/g, '')) || 0,
        net_price: parseFloat(formData.sellerPrice?.replace(/\s/g, '')) || 0,
        commission: parseFloat(formData.agencyCommission?.replace(/\s/g, '')) || 0,
        status: property.status,
        // ⚠️ NE PAS envoyer transactionType pour préserver le status
        area: parseFloat(formData.livingArea || formData.totalArea || '0'),
        rooms: parseInt(formData.roomCount || '1'),
        bedrooms: parseInt(formData.bedroomCount || '0'),
        living_room_area: formData.livingRoomArea ? parseFloat(formData.livingRoomArea) : null,
        usable_area: formData.usableArea ? parseFloat(formData.usableArea) : null,
        bathroom_count: parseInt(formData.bathroomCount || formData.bathrooms || '1'),
        floor: formData.floor ? parseInt(formData.floor) : null,
        total_floors: formData.totalFloors ? parseInt(formData.totalFloors) : null,
        is_ground_floor: formData.isGroundFloor || false,
        is_top_floor: formData.isTopFloor || false,
        construction_year: formData.constructionYear ? parseInt(formData.constructionYear) : null,
        general_condition: formData.condition || formData.generalCondition || null,
        kitchen_type: formData.kitchenType || null,
        view: formData.view || null,
        has_elevator: formData.hasElevator || false,
        has_parking: formData.hasParking || false,
        has_balcony: formData.hasBalcony || false,
        has_terrace: formData.hasTerrace || false,
        has_air_conditioning: formData.hasAirConditioning || false,
        has_garden: formData.hasGarden || false,
        has_pool: formData.hasPool || false,
        has_sea_view: formData.hasSeaView || false,
        has_mountain_view: formData.hasMountainView || false,
        has_garage: formData.hasGarage || false,
        has_cellar: formData.hasCellar || false,
        has_attic: formData.hasAttic || false,
        has_guardian: formData.hasGuardian || false,
        has_intercom: formData.hasIntercom || false,
        has_interphone: formData.hasInterphone || false,
        has_digicode: formData.hasDigicode || false,
        has_trustee: formData.hasTrustee || false,
        has_alarm: formData.hasAlarm || false,
        has_video_surveillance: formData.hasVideoSurveillance || false,
        has_fiber: formData.hasFiber || false,
        has_roof_terrace: formData.hasRoofTerrace || false,
        has_gas_heating: formData.hasGasHeating || false,
        has_private_parking: formData.hasPrivateParking || false,
        has_cistern: formData.hasCistern || false,
        has_water_tank: formData.hasWaterTank || false,
        has_double_glazing: formData.hasDoubleGlazing || false,
        has_electric_shutters: formData.hasElectricShutters || false,
        is_concierge: formData.is_concierge || false,
        heating_type: formData.heatingType || 'Non spécifié',
        orientation: formData.orientation || null,
        exposure: formData.exposure || null,
        floor_level: formData.floorLevel || null,
        building_condition: formData.buildingCondition || null,
        accessibility: formData.accessibility || null,
        energy_class: formData.energyClass || null,
        co2_emission: formData.co2Emission || null,
        description: formData.advertisingText || formData.showcaseText || null,
        listing_title: formData.advertisingTitle || null,
        showcase_text: formData.showcaseText || null,
        keys_location: formData.keyLocation || null,
        observations: formData.observations || null,
        owner_name: `${formData.ownerCivility || ''} ${formData.ownerFirstName || ''} ${formData.ownerLastName || ''}`.trim(),
        owner_address: formData.ownerAddress || null,
        owner_wilaya: formData.ownerWilaya || null,
        owner_city: formData.ownerCity || null,
        owner_phone: formData.ownerPhone || null,
        owner_email: formData.ownerEmail || null,
        reference_number: formData.reference || null,
        agent_id: formData.agent ? parseInt(formData.agent) : null,
        publish_dar_dz: formData.publishToDarDz || false,
        publish_ouedkniss: formData.publishToOuedkniss || false,
        publish_external_site: formData.publishExternalSite || false,
        photos: photoUrls,
      };

      // Valider et normaliser les données (en mode édition)
      const propertyData = validatePropertyData(rawPropertyData, true);

      console.log('🔍 [EDIT-FORM] Status après validation:', propertyData.status);
      console.log('🔍 [EDIT-FORM] Données après normalisation:');
      console.log('  📋 general_condition:', propertyData.general_condition);
      console.log('  📋 living_room_area:', propertyData.living_room_area);
      console.log('  📋 total_floors:', propertyData.total_floors);
      console.log('  📋 kitchen_type:', propertyData.kitchen_type);
      console.log('  📋 bathrooms:', propertyData.bathrooms);
      console.log('  📋 has_roof_terrace:', propertyData.has_roof_terrace);
      console.log('  📋 has_gas_heating:', propertyData.has_gas_heating);
      console.log('  📋 has_private_parking:', propertyData.has_private_parking);
      console.log('  📋 has_digicode:', propertyData.has_digicode);
      console.log('  📋 has_cistern:', propertyData.has_cistern);
      console.log('  📋 has_water_tank:', propertyData.has_water_tank);
      console.log('  📋 has_double_glazing:', propertyData.has_double_glazing);
      console.log('  📋 has_electric_shutters:', propertyData.has_electric_shutters);
      console.log('Données à envoyer pour la mise à jour:', propertyData);

      try {
        const success = await updateProperty(parseInt(id!), propertyData);

        if (success) {
          // Si le bien est publié sur Dardz, le re-synchroniser automatiquement
          if (formData.publishToDarDz) {
            try {
              const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
              const syncResponse = await fetch(`${baseUrl}/sync-to-dardz/${id}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
              });

              if (syncResponse.ok) {
                console.log('✅ Bien mis à jour sur Dardz');
              } else {
                console.error('❌ Erreur mise à jour Dardz:', await syncResponse.text());
              }
            } catch (error) {
              console.error('❌ Erreur mise à jour Dardz:', error);
            }
          }

          // Rediriger vers la liste appropriée selon le statut
          const redirectPath = {
            'for-sale': '/en-vente',
            'for-rent': '/en-location',
            'for-short-rent': '/location-courte-duree',
            'estimation': '/estimations',
            'sold': '/vendus',
            'rented': '/loues',
            'archived': '/archives'
          }[property.status] || '/';

          navigate(redirectPath);
        } else {
          alert('Erreur lors de la modification du bien. Vérifiez votre connexion.');
        }
      } catch (error) {
        console.error('Erreur détaillée:', error);
        alert(`Erreur lors de la modification du bien: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Une erreur est survenue lors de la sauvegarde du bien');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralInfo onNext={goToNextTab} />;
      case 'details':
        return <PropertyDetails onNext={goToNextTab} />;
      case 'criteria':
        return <Criteria onNext={goToNextTab} />;
      case 'description':
        return <Description onNext={goToNextTab} />;
      case 'photos':
        return <Photos onNext={goToNextTab} />;
      case 'documents':
        return <Documents onSave={handleSave} />;
      case 'matching':
        return <Matching onSave={handleSave} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            Chargement du bien...
          </p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Bien non trouvé
          </p>
          <Button onClick={handleBack} className="mt-4">
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <Button
            variant="ghost"
            className="p-2"
            onClick={handleBack}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Modifier le bien - {property.type}
          </h1>
        </div>

        <div className="rounded-lg bg-white shadow-sm dark:bg-gray-800">
          <PropertyFormTabs 
            activeTab={activeTab} 
            onTabChange={setActiveTab}
            onSave={handleSave}
            isSubmitting={isSubmitting}
          />
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export const PropertyEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { getPropertyById, updateProperty, properties, isLoading: propertiesLoading } = useProperties();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [property, setProperty] = useState<any>(null);

  useEffect(() => {
    // Attendre que les propriétés soient chargées
    if (propertiesLoading) {
      return;
    }

    if (id) {
      // Si les propriétés ne sont pas encore là, attendre un peu plus
      if (properties.length === 0) {
        console.log('PropertyEditForm - Properties still empty, waiting...');
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return;
      }

      // Utiliser l'ID comme string (pas besoin de conversion)
      const foundProperty = getPropertyById(id);
      
      if (foundProperty) {
        console.log('PropertyEditForm - Property found:', foundProperty);
        setProperty(foundProperty);
      } else {
        console.log('PropertyEditForm - Available IDs:', properties.map(p => p.id));
        console.log('PropertyEditForm - Looking for ID:', id);
        console.log('PropertyEditForm - Property not found, redirecting...');
        navigate('/');
      }
      setIsLoading(false);
    }
  }, [id, getPropertyById, navigate, propertiesLoading, properties]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            Chargement du bien...
          </p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Bien non trouvé
          </p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PropertyFormProvider initialProperty={property}>
      <PhotoProvider initialPhotos={property?.photos || []}>
        <DocumentProvider>
          <PropertyEditFormContent />
        </DocumentProvider>
      </PhotoProvider>
    </PropertyFormProvider>
  );
};